import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { navigate } from 'gatsby';
import React from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import { ConfirmationDialog } from '@components/ConfirmationDialog';
import Icon from '@components/Icon/Icon';
import SkeletonLoader from '@components/SkeletonLoader/SkeletonLoader';

import { CLIENTS } from '@constants/routes';

import { useDeleteAccount, useGetAccountUsers } from '@hooks/api/account';
import { useCuration } from '@hooks/useCuration';

import type { Profile } from '@models/session';

import { formatName } from '@utils/format';

const messages = defineMessages({
  title: {
    id: 'dangerZone.title',
    defaultMessage: 'Danger Zone',
  },
  'deleteAccount.members': {
    id: 'dangerZone.deleteAccount.members',
    defaultMessage: 'The following account users will be deleted',
  },
  'deleteAccount.title': {
    id: 'dangerZone.deleteAccount.title',
    defaultMessage: 'Delete Account',
  },
  'deleteAccount.subtitle': {
    id: 'dangerZone.deleteAccount.subtitle',
    defaultMessage:
      'Once you delete an account, there is no going back. Please be certain.',
  },
  'deleteAccount.description': {
    id: 'dangerZone.deleteAccount.description',
    defaultMessage:
      'This action will permanently delete all data for every user in the account. This includes: account information, user logins, and patient records. Once you delete an account, there is no going back. Please be certain.',
  },
  passwordChallenge: {
    id: 'dangerZone.passwordChallenge',
    defaultMessage: 'To delete this account, enter your password',
  },
});

interface DangerZoneProps {
  /**
   * ID of the user to perform dangerous actions on
   */
  user?: Pick<Profile, 'user_id' | 'account_id'>;
}

/**
 * Perform dangerous actions such as delete account
 */
function DangerZone({ user }: DangerZoneProps) {
  const { formatMessage } = useIntl();
  const { reset } = useCuration();

  const { data: usersData, loading: usersLoading } = useGetAccountUsers(
    user?.account_id
  );
  const accountUsers = usersData;

  const [deleteAccount, deleteAccountLoading] = useDeleteAccount();

  const loading = usersLoading;

  const handleDeleteAccount = async (password: string) => {
    if (!user || deleteAccountLoading) {
      return;
    }
    await deleteAccount(
      {
        input: {
          account_id: user.account_id,
          password,
        },
      },
      accountUsers ?? []
    );
    reset();
    navigate(CLIENTS);
  };

  return (
    <SkeletonLoader loading={loading}>
      <Card>
        <CardHeader
          title={formatMessage(messages.title)}
          slotProps={{
            title: { sx: { color: (theme) => theme.palette.error.main } },
          }}
        />
        <Divider />
        <CardContent>
          <ListItem>
            <ListItemText
              primary={formatMessage(messages['deleteAccount.title'])}
              secondary={formatMessage(messages['deleteAccount.subtitle'])}
            />
            <ConfirmationDialog
              challenge={{
                phrase: '',
                description: formatMessage(messages.passwordChallenge),
                type: 'password',
              }}
              content={
                <Stack spacing={2}>
                  <Typography>
                    {formatMessage(messages['deleteAccount.description'])}
                  </Typography>
                  <>
                    <Typography>
                      {formatMessage(messages['deleteAccount.members'])}:
                    </Typography>
                    <ul>
                      {accountUsers?.map(
                        ({ user_id, first_name, last_name }) => (
                          <li key={user_id}>
                            {formatName(first_name, last_name)}
                          </li>
                        )
                      )}
                    </ul>
                  </>
                </Stack>
              }
              onConfirm={handleDeleteAccount}
              title={formatMessage(messages['deleteAccount.title'])}
            >
              {({ handleOpen }) => (
                <Button
                  color="error"
                  onClick={handleOpen}
                  startIcon={<Icon name="trash" />}
                >
                  {formatMessage(messages['deleteAccount.title'])}
                </Button>
              )}
            </ConfirmationDialog>
          </ListItem>
        </CardContent>
      </Card>
    </SkeletonLoader>
  );
}

export default withErrorBoundary(DangerZone, {
  fallback: null,
});
